// A Repo on the subduction line constructs its Subduction engine at construction;
// this entry initializes that WASM synchronously for this runtime.
import "@automerge/automerge-subduction";
import { BrowserWebSocketClientAdapter } from "@automerge/automerge-repo-network-websocket";
import { AutomergeUrl, IndexedDBStorageAdapter, Repo } from "@automerge/react";
import { SocketResumeMonitor } from "den/client/socket-resume";

export function isAutomergeUrl(
  value: string | undefined,
): value is AutomergeUrl {
  return typeof value === "string" && value.startsWith("automerge:");
}

function getSyncUrl(): string {
  if (typeof window !== "undefined") {
    // Same origin as the page, in every environment. In production the API is
    // same-origin already; in dev the Vite server proxies `/api`, this upgrade
    // included (`server.proxy.ws` in vite.config.ts).
    //
    // Dev used to address the API's own port directly, because the upgrade
    // did not survive the proxy. That only worked when the page was on
    // localhost: over a dev tunnel the browser is on :443 of a hostname that
    // routes to Vite alone, so `wss://<tunnel-host>:3000/…` reached nothing.
    // (The upgrade fails under `bunx --bun vite`, not because of the proxy
    // config, which is why dev.ts runs this Vite server on Node.)
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProtocol}//${window.location.host}/api/projects/demo/documents/sync`;
  }
  return "ws://localhost:3000/api/projects/demo/documents/sync";
}

const wsAdapter = new BrowserWebSocketClientAdapter(getSyncUrl());

// Half-open sockets (laptop sleep, network flap) don't fire `onclose`, so the
// adapter's reconnect logic never trips. Probing on visibility + online events
// catches those without mistaking an idle-but-healthy socket for a dead one:
// server-sent protocol pings keep the connection alive but are invisible to JS,
// so silence is not evidence of anything. The adapter's `socket` field is real
// but untyped upstream, hence the cast.
new SocketResumeMonitor(wsAdapter as unknown as { socket?: WebSocket }).start();

export const repo = new Repo({
  storage: new IndexedDBStorageAdapter(),
  network: [wsAdapter],
});
